import React from 'react';
import {
  Loader,
  Icon,
  Table,
  Button,
  Header,
  Label,
  Checkbox,
} from 'semantic-ui-react';
import moment from 'moment';
import FileSaver from 'file-saver';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Area,
  Tooltip as TooltipChart,
} from 'recharts';

import SelectAssetInReport from '../SelectAssetInReport';
import { alertMessage } from '../../../helper/alert-message';
import {
  reportDownloadExcel,
  reportDownloadPdf,
  reportView,
} from '../helper/dataService';
import { getIdsAndTime, getDuration, getOrg } from '../helper/helper';
import reportsEnv from '../reportsEnv';

const { durationLimit, assetLimit, withLocationDurationLimit } =
  reportsEnv.ignition;

class Ignition extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      active: false,
      pdfDisableBtn: false,
      excelDisableBtn: false,
      selectedAssets: [],
      data: [],
      filteredEngineData: [],
      startTime: moment().startOf('day'),
      endTime: moment().endOf('day'),
      graphList: [],
      withLocation: false,
      withDayWise: false,
    };
  }

  componentDidMount() {
    const {
      selectedAssets = [],
      startTime,
      endTime,
    } = JSON.parse(window.localStorage.getItem('reports_state'));

    this.setState({
      selectedAssets,
      startTime: moment(startTime),
      endTime: moment(endTime),
    });
  }

  getBack = () => {
    this.setState({
      data: [],
    });
  };

  downloadReport = () => {
    this.setState({
      active: true,
      pdfDisableBtn: false,
      excelDisableBtn: false,
    });

    const { ids, startTime, endTime } = getIdsAndTime(this.state);
    const { withLocation, withDayWise } = this.state;
    let { viewPath, apiVer } = reportsEnv.ignition;
    if (withDayWise) {
      viewPath = 'ignition_base_v3';
      apiVer = 'v1.3';
    }

    reportView(
      { viewPath, apiVer },
      {
        asset_ids: ids,
        from: startTime,
        to: endTime,
        speed_limit: 5,
        location: withLocation,
      },
    )
      .then((response) => {
        this.setState({
          data: response.data,
          graphList: response.data.map((asset) => {
            let filteredList = [];
            asset.ignition_status.forEach((item) => {
              filteredList.push({
                awake: Math.ceil(item.in_seconds / 60),
                startAt: item.ignition_stopped_at
                  ? moment(item.ignition_stopped_at.$date).format('DD MMM')
                  : null,
              });
            });
            return filteredList;
          }),
          active: false,
        });
      })
      .catch((error) => {
        this.setState({ active: false });
        alertMessage(`Sorry! Something went wrong`);
      });
  };

  handleDownloadPdfBtn = () => {
    this.setState({ pdfDisableBtn: true });

    const { ids, startTime, endTime } = getIdsAndTime(this.state);
    const { withLocation, withDayWise } = this.state;
    let { pdfPath, apiVer } = reportsEnv.ignition;
    if (withDayWise) {
      pdfPath = 'ignition_pdf_v3';
      apiVer = 'v1.3';
    }

    reportDownloadPdf(
      { pdfPath, apiVer },
      {
        asset_ids: ids,
        from: startTime,
        to: endTime,
        speed_limit: 5,
        location: withLocation,
      },
    )
      .then((response) => {
        FileSaver.saveAs(
          response.data,
          `Finder_Ignition_Report_${moment().format(
            'DD-MMMM-YYYY_hh:mm A',
          )}.pdf`,
        );
        this.setState({ pdfDisableBtn: false });
      })
      .catch((error) => {
        this.setState({ pdfDisableBtn: false });
        alertMessage(`Sorry! Something went wrong`);
      });
  };

  handleDownloadExcelBtn = () => {
    this.setState({ excelDisableBtn: true });

    const { ids, startTime, endTime } = getIdsAndTime(this.state);
    const { withLocation, withDayWise } = this.state;
    let { excelPath, apiVer } = reportsEnv.ignition;
    if (withDayWise) {
      excelPath = 'ignition_excel_v3';
      apiVer = 'v1.3';
    }

    reportDownloadExcel(
      { excelPath, apiVer },
      {
        asset_ids: ids,
        from: startTime,
        to: endTime,
        speed_limit: 5,
        location: withLocation,
      },
    )
      .then((response) => {
        FileSaver.saveAs(
          response.data,
          `Finder_Ignition_Report_${moment().format(
            'DD-MMMM-YYYY_hh:mm A',
          )}.xlsx`,
        );
        this.setState({ excelDisableBtn: false });
      })
      .catch((error) => {
        this.setState({ excelDisableBtn: false });
        alertMessage(`Sorry! Something went wrong`);
      });
  };

  render() {
    const {
      selectedAssets,
      data,
      active,
      pdfDisableBtn,
      excelDisableBtn,
      startTime,
      endTime,
      graphList,
      withLocation,
      withDayWise,
    } = this.state;

    const finalDurationLimit = withLocation
      ? withLocationDurationLimit
      : durationLimit;

    const duration = getDuration(endTime, startTime);
    const assetsQuantity = selectedAssets.length;

    const isDurationLimitCross = duration > finalDurationLimit;
    const isAssetLimitCross = assetsQuantity > assetLimit;

    if (isDurationLimitCross) {
      alertMessage(
        `You can't select this report more than ${finalDurationLimit} days.`,
      );
    } else if (isAssetLimitCross) {
      alertMessage(
        `You can't select this report more than ${assetLimit} assets.`,
      );
    }

    const title = `Ignition [${finalDurationLimit} days][${assetLimit} asset]`;
    const isValidDuration =
      duration > 0 && endTime.isSameOrAfter(startTime) && !isDurationLimitCross;
    const isValidAsset = assetsQuantity > 0 && !isAssetLimitCross;

    return (
      <div>
        {!active && data.length <= 0 && (
          <div className=' center-report'>
            <Header as='h2'>{title}</Header>

            <div
              style={{
                margin: '20px 0',
              }}
            >
              <Label.Group size='large'>
                <Label>
                  <Icon name='car' />
                  {assetsQuantity} assets
                </Label>
                <Label>
                  <Icon name='calendar' />
                  {duration} days
                </Label>
                <Label>
                  <Icon name='play' />
                  {moment(startTime).format('dddd, MMMM Do, YYYY, hh:mm:ss A')}
                </Label>
                <Label>
                  <Icon name='stop' />
                  {moment(endTime).format('dddd, MMMM Do, YYYY, hh:mm:ss A')}
                </Label>
              </Label.Group>
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              {reportsEnv.ignition.withLocationOrganizations.includes(
                getOrg(),
              ) ? (
                <Checkbox
                  style={{ margin: '20px' }}
                  label='With Location?'
                  checked={withLocation}
                  onChange={() =>
                    this.setState({ withLocation: !this.state.withLocation })
                  }
                />
              ) : null}

              <Checkbox
                style={{ margin: '20px' }}
                label='With Day Wise?'
                checked={withDayWise}
                onChange={() =>
                  this.setState({ withDayWise: !this.state.withDayWise })
                }
              />
            </div>

            {isValidDuration && isValidAsset && (
              <Button.Group>
                <Button
                  color='black'
                  style={{ width: '200px', fontSize: '16px' }}
                  onClick={this.downloadReport}
                  icon='eye'
                  content='VIEW HERE'
                  disabled={pdfDisableBtn || excelDisableBtn}
                />

                <Button
                  color='teal'
                  style={{ width: '200px', fontSize: '16px' }}
                  icon='file pdf outline'
                  content='Download PDF'
                  onClick={this.handleDownloadPdfBtn}
                  disabled={pdfDisableBtn || excelDisableBtn}
                  loading={pdfDisableBtn}
                />

                <Button
                  color='teal'
                  style={{ width: '200px', fontSize: '16px' }}
                  icon='file excel outline'
                  content='Download Excel'
                  onClick={this.handleDownloadExcelBtn}
                  disabled={excelDisableBtn || pdfDisableBtn}
                  loading={excelDisableBtn}
                />
              </Button.Group>
            )}
          </div>
        )}

        {!active && data.length > 0 ? (
          <div style={{ margin: '2%' }}>
            <div className='left-right'>
              <div>
                <Header as='h2'>Ignition</Header>
                <p>
                  Generated on: {moment().format('MMMM Do, YYYY, hh:mm:ss A')}
                </p>
              </div>
              <Button primary onClick={this.getBack}>
                Back
              </Button>
            </div>

            <br />
            {data.map((item, i) => (
              <div key={i} id={item.asset_detail.name}>
                <div className='ui stackable grid'>
                  <div className='eight wide column' style={{ paddingLeft: 0 }}>
                    <div className='ui card fluid'>
                      <div className='content'>
                        <div className='header'>{item.asset_detail.name}</div>

                        <div className='description'>
                          <div>
                            <p>
                              <i className='arrow right icon' /> From:
                              {moment(startTime).format(
                                'dddd, MMMM Do, YYYY, hh:mm:ss A',
                              )}
                            </p>
                            <p>
                              <i className='arrow right icon' /> Till:{' '}
                              {moment(endTime).format(
                                'dddd, MMMM Do, YYYY, hh:mm:ss A',
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className='eight wide column'
                    style={{ paddingRight: 0 }}
                  >
                    <div className='ui card fluid'>
                      <ul className='content'>
                        <div className='ui left aligned icon header'>
                          • Total ignition on time:{' '}
                          {item.asset_detail.total_ignition_on_time ||
                            'Not Available'}
                        </div>

                        <div className='ui left aligned icon header'>
                          • While stopped (under 5 km/h):{' '}
                          {item.asset_detail.ignition_running_under_limit ||
                            'Not Available'}
                        </div>

                        <div className='ui left aligned icon header'>
                          • While running:{' '}
                          {item.asset_detail.ignition_on_over_limit ||
                            'Not Available'}
                        </div>

                        <div className='ui left aligned icon header'>
                          • Total ignition on{' '}
                          {item.asset_detail.total_engine_on || '0'} times.
                        </div>
                      </ul>
                    </div>
                  </div>
                </div>

                {graphList[i].length > 1 ? (
                  <ResponsiveContainer height={200} width='100%'>
                    <ComposedChart data={graphList[i]}>
                      <CartesianGrid stroke='#ced4da' />
                      <XAxis dataKey='stoppedAt' />
                      <YAxis />
                      <TooltipChart />
                      <Legend />
                      <Area
                        type='monotone'
                        dataKey='awake'
                        name='Awake Time (Minutes)'
                        stroke='#12b886'
                        fill='#12b886'
                        dot={false}
                        isAnimationActive={true}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : null}

                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Ignition Started Time</Table.HeaderCell>
                      <Table.HeaderCell>Ignition Stopped Time</Table.HeaderCell>
                      <Table.HeaderCell>
                        Ignition Awake Duration
                      </Table.HeaderCell>
                      {withLocation ? (
                        <Table.HeaderCell>Location</Table.HeaderCell>
                      ) : null}
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {item.ignition_status.map((last, idx) => {
                      return (
                        <Table.Row key={idx}>
                          <Table.Cell>
                            {last.ignition_starts_at
                              ? moment(last.ignition_starts_at.$date).format(
                                  'dddd, MMMM Do, YYYY, hh:mm:ss A',
                                )
                              : ''}
                          </Table.Cell>
                          <Table.Cell>
                            {last.ignition_stopped_at
                              ? moment(last.ignition_stopped_at.$date).format(
                                  'dddd, MMMM Do, YYYY, hh:mm:ss A',
                                )
                              : ''}
                          </Table.Cell>
                          <Table.Cell>{last.ignition_awake}</Table.Cell>
                          {withLocation ? (
                            <Table.Cell>{last.engine_start_loc}</Table.Cell>
                          ) : null}
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
                <br />
              </div>
            ))}
            {data.length > 1 ? <SelectAssetInReport assetList={data} /> : null}
          </div>
        ) : (
          <Loader active={active} inline='centered'>
            {' '}
            Generating...
          </Loader>
        )}
      </div>
    );
  }
}

export default Ignition;
